import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { withApollo } from '@apollo/react-hoc';
import { withRouter } from 'react-router';
import { compose, branch, renderNothing, renderComponent, setDisplayName } from 'react-recompose';
import { connect } from 'react-redux';
import { connectedRouterRedirect } from 'redux-auth-wrapper/history3/redirect';
import Loader from '../../components/Loader';
import communityProfileQuery from './community-profile-query';

export const isUserAuthenticated = (state) => (
  get(state, 'current.viewer.isAuthenticated')
);

export const authorized = (options={}) =>
  compose(
    connect((state) => ({ viewer: state.current.viewer })),
    setDisplayName('authorized'),
    branch(
      ({ viewer }) => !viewer.isAuthenticated,
      renderComponent(options.failure || renderNothing()),
    ),
  );

export const authorizedOrLogin = compose(
  withRouter,
  connectedRouterRedirect({
    wrapperDisplayName: 'authorizedOrLogin',
    authenticatedSelector: (state) => get(state, 'current.viewer.isAuthenticated'),
    redirectPath: '/login',
  }),
);

export const notAuthorized = compose(
  withRouter,
  connectedRouterRedirect({
    wrapperDisplayName: 'NotAuthorized',
    authenticatedSelector: (state) => !get(state, 'current.viewer.isAuthenticated'),
    redirectPath: (state, props) => props.location.query.redirect || '/',
    allowRedirectBack: false,
  }),
);

export const authorizedWithRole = (roles, options={}) =>
  compose(
    connect((state) => pick(state, 'current')),
    setDisplayName('authorizedWithRole'),
    branch(
      ({ current }) => !current.viewer.isAuthenticated || ![].concat(roles).includes(get(current, 'viewer.idToken["custom:role"]')),
      renderComponent(options.failure || renderNothing()),
    ),
  );

export const authorizedImpersonatorWithRole = (roles, options) =>
  compose(
    connect((state) => pick(state, 'current')),
    setDisplayName('authorizedImpersonatorWithRole'),
    branch(
      ({ current }) => current.viewer.impersonatorToken && !current.viewer.impersonatorToken.roles.some((r) => roles.includes(r)),
      renderComponent(options.failure || renderNothing()),
    ),
  );

export const requireCommunityProfile = (Component) => {
  class ProfileAuthorizer extends React.PureComponent {
    static propTypes = {
      client: PropTypes.shape({
        readQuery: PropTypes.func.isRequired,
        query: PropTypes.func.isRequired,
      }).isRequired,
      router: PropTypes.shape({
        push: PropTypes.func.isRequired,
      }).isRequired,
    };

    constructor(props) {
      super(props);
      const { client } = this.props;
      // read field_activated_account from cache without triggering server request
      // no need to make requests if profile is activated
      try {
        const { viewer } = client.readQuery({
          query: communityProfileQuery,
        });
        const hasServerlessCC = viewer.enabledFeatures?.includes('COMMUNITYSERVERLESS');
        if (get(viewer, 'profile.field_activated_account') && (!hasServerlessCC || get(viewer, 'profile.field_account_consent'))) {
          this.state = { render: true };
          return;
        }
      } catch (e) {
        // errors = no active profile
      }
      this.state = { loading: true };
    }

    async componentDidMount() {
      const { client, router } = this.props;
      const { loading } = this.state;
      if (loading) {
        const response = await client.query({
          query: communityProfileQuery,
          fetchPolicy: 'network-only',
        });
        const state = { loading: false };
        const hasServerlessCC = get(response, 'data.viewer.enabledFeatures')?.includes('COMMUNITYSERVERLESS');
        try {
          if (get(response, 'data.viewer.profile.field_activated_account') && (!hasServerlessCC || get(response, 'data.viewer.profile.field_account_consent'))) {
            state.render = true;
            return; // skip router.push()
          }
        } finally {
          // eslint-disable-next-line react/no-did-mount-set-state
          this.setState(state);
        }
        if (hasServerlessCC) {
          router.push('/counselorcommunity/');
        } else {
          router.push('/counselor-community/');
        }
      }
    }

    render() {
      const { render, loading } = this.state;
      if (loading) {
        return <Loader active />;
      } else if (render) {
        return <Component {...this.props} />;
      }
      return null;
    }
  }

  return compose(
    authorized(),
    withApollo,
    withRouter,
  )(ProfileAuthorizer);
};
